import { useSession } from 'next-auth/react';
import useSWR from 'swr';

import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { NEXT_AUTH_STATUSES } from '@/lib/constants';
import { FEATURE_FLAGS } from '@/lib/constants/feature-flags';
import { ShipmentMeta } from '@/lib/models/shipment/types';
import { ShipmentMetaApiResponse } from '@/types/api-types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getShipmentMetasUrl } from '@/utils/helpers/api-urls';

const ERROR_MESSAGE = 'Error fetching shipment meta';

interface UseShipmentMetaProps {
  shipmentId?: string | null;
}

interface UseShipmentMeta extends AsyncDataInterface {
  shipmentMeta: ShipmentMeta | null;
}

export const useShipmentMeta = ({ shipmentId }: UseShipmentMetaProps): UseShipmentMeta => {
  const { status } = useSession();
  const { variant: isShipmentMetaEnabled } = useFeatureFlag(FEATURE_FLAGS.SHIPMENT_META);
  const isEnabled = status === NEXT_AUTH_STATUSES.AUTHENTICATED && shipmentId && isShipmentMetaEnabled === true;

  const { isLoading, error, data } = useSWR<ShipmentMetaApiResponse>(
    isEnabled ? getShipmentMetasUrl(shipmentId) : null,
    request,
    {
      revalidateOnFocus: false,
      errorRetryCount: 2,
    },
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  return {
    isLoading,
    shipmentMeta: data?.shipmentMeta || null,
    error: errorMessage,
  };
};
